import { useState, useMemo, useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { AnalyticsEvents } from '@pantry/analytics';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
  cn,
} from '@pantry/ui';
import {
  useProductStats,
  useResetTour,
  type ProductStatsParams,
  type ProductStatsItem,
} from '@pantry/api-client';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import { useTourContext } from '../../components/onboarding/useTourContext';
import {
  ChartBarIcon,
  FunnelIcon,
  CalendarDaysIcon,
  ArchiveBoxIcon,
  CheckIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ArrowsUpDownIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ProductStatsDialog } from '../../components/products/ProductStatsDialog';
import { FlowRing } from '../../components/statistics/FlowRing';

export const Route = createFileRoute('/_app/statistics')({
  component: ProductStatsPage,
});

type TimeframeOption = 'all' | 'current' | 'custom';
type SortOption = ProductStatsParams['sortBy'];
type SortOrder = ProductStatsParams['order'];
type StatisticsMiniTourId = 'statistics-basics' | 'statistics-controls';

const DEMO_STATS_PRODUCTS: ProductStatsItem[] = [
  {
    productId: 'demo-stats-1',
    productName: 'Greek Yogurt',
    productNames: null,
    productBrand: 'Demo Brand',
    productImageUrl: null,
    productCategory: 'Dairy',
    currentStock: 2,
    activeInstanceCount: 2,
    totalPurchased: 8,
    totalConsumed: 6,
    totalWasted: 2,
    totalSpent: 3699,
    wasteRate: 25,
    wasteReasons: {
      expired: 1,
      spoiled: 1,
      damaged: 0,
      disliked: 0,
      overstock: 0,
      other: 0,
    },
    avgTimeToConsumeDays: 4,
    avgPricePerUnit: 4.62,
    lastPurchaseDate: '2026-01-20',
  },
  {
    productId: 'demo-stats-2',
    productName: 'Spinach',
    productNames: null,
    productBrand: 'Farm Fresh',
    productImageUrl: null,
    productCategory: 'Produce',
    currentStock: 1,
    activeInstanceCount: 1,
    totalPurchased: 10,
    totalConsumed: 8,
    totalWasted: 2,
    totalSpent: 2290,
    wasteRate: 20,
    wasteReasons: {
      expired: 1,
      spoiled: 1,
      damaged: 0,
      disliked: 0,
      overstock: 0,
      other: 0,
    },
    avgTimeToConsumeDays: 3,
    avgPricePerUnit: 2.29,
    lastPurchaseDate: '2026-01-25',
  },
  {
    productId: 'demo-stats-3',
    productName: 'Chicken Breast',
    productNames: null,
    productBrand: 'Butcher Choice',
    productImageUrl: null,
    productCategory: 'Protein',
    currentStock: 0,
    activeInstanceCount: 0,
    totalPurchased: 6,
    totalConsumed: 5,
    totalWasted: 1,
    totalSpent: 4890,
    wasteRate: 16.7,
    wasteReasons: {
      expired: 1,
      spoiled: 0,
      damaged: 0,
      disliked: 0,
      overstock: 0,
      other: 0,
    },
    avgTimeToConsumeDays: 5,
    avgPricePerUnit: 8.15,
    lastPurchaseDate: '2026-01-10',
  },
];

const DEMO_STATS_TOTALS = {
  totalProducts: DEMO_STATS_PRODUCTS.length,
  totalPurchased: DEMO_STATS_PRODUCTS.reduce((sum, product) => sum + product.totalPurchased, 0),
  totalConsumed: DEMO_STATS_PRODUCTS.reduce((sum, product) => sum + product.totalConsumed, 0),
  totalWasted: DEMO_STATS_PRODUCTS.reduce((sum, product) => sum + product.totalWasted, 0),
  totalSpent: DEMO_STATS_PRODUCTS.reduce((sum, product) => sum + product.totalSpent, 0),
  overallWasteRate: 20.8,
} as const;

function ProductStatsPage() {
  const { t, i18n } = useLingui();
  const { selectedHousehold, isLoading: householdLoading } = useSelectedHousehold();
  const householdId = selectedHousehold?.id || '';
  const { track } = useAnalytics();
  const { startTour, isActive: isTourActive, currentTourId } = useTourContext();
  const resetTour = useResetTour();
  const hasTrackedRef = useRef(false);

  // Timeframe state (defined before tracking effect so we can use it)
  const [timeframe, setTimeframe] = useState<TimeframeOption>('all');

  // Track statistics viewed on mount (only once)
  useEffect(() => {
    if (householdId && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      track(AnalyticsEvents.STATISTICS_VIEWED, {
        household_id: householdId,
        time_period: timeframe,
      });
    }
  }, [householdId, track, timeframe]);
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>(undefined);
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>(undefined);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState<SortOption>('purchased');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Selected product for detail dialog
  const [selectedProduct, setSelectedProduct] = useState<ProductStatsItem | null>(null);
  const [startingMiniTourId, setStartingMiniTourId] = useState<StatisticsMiniTourId | null>(null);

  // Build query params
  const queryParams = useMemo<ProductStatsParams>(() => {
    const params: ProductStatsParams = {
      sortBy,
      order: sortOrder,
    };

    if (timeframe === 'current') {
      params.currentOnly = true;
    } else if (timeframe === 'custom') {
      if (customDateFrom) {
        params.from = format(customDateFrom, 'yyyy-MM-dd');
      }
      if (customDateTo) {
        params.to = format(customDateTo, 'yyyy-MM-dd');
      }
    }

    return params;
  }, [timeframe, customDateFrom, customDateTo, sortBy, sortOrder]);

  const {
    data: statsData,
    isLoading: statsLoading,
    error,
  } = useProductStats(householdId, queryParams);

  const isLoading = householdLoading || statsLoading;
  const hasProducts = !!(statsData && statsData.products.length > 0);
  const isStatisticsTourActive =
    isTourActive &&
    (currentTourId === 'statistics-basics' || currentTourId === 'statistics-controls');
  const isStatisticsTourStarting = startingMiniTourId !== null;
  const shouldShowGhostStats =
    !error && !hasProducts && (isStatisticsTourActive || isStatisticsTourStarting);
  const displayTotals = shouldShowGhostStats ? DEMO_STATS_TOTALS : statsData?.totals;
  const displayProducts = shouldShowGhostStats ? DEMO_STATS_PRODUCTS : (statsData?.products ?? []);

  const handleStartMiniTour = async (tourId: StatisticsMiniTourId) => {
    if (startingMiniTourId) return;

    setStartingMiniTourId(tourId);
    try {
      await resetTour.mutateAsync({ tourId, deviceType: 'web' });
      await startTour(tourId);
    } catch (tourError) {
      console.error(`Failed to start statistics mini tour ${tourId}:`, tourError);
    } finally {
      setStartingMiniTourId(null);
    }
  };

  // Show empty state if no household selected
  if (!householdLoading && !selectedHousehold) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="rounded-2xl max-w-md">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
              <ChartBarIcon className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-display text-xl font-semibold text-foreground">
              <Trans>No Household Selected</Trans>
            </h3>
            <p className="mt-2 text-muted-foreground">
              <Trans>Select a household to view product statistics.</Trans>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8" data-tour="statistics-page-intro">
      {/* Header */}
      <div className="animate-in" style={{ animationDelay: '0ms' }} data-tour="statistics-header">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-4 items-center">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10">
              <ChartBarIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold text-foreground">
                <Trans>Product Statistics</Trans>
              </h1>
              <p className="text-muted-foreground">
                <Trans>Track consumption, waste, and spending</Trans>
              </p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-tour="statistics-help"
                aria-label={t`Open statistics tour help`}
                title={t`Statistics help`}
                aria-busy={startingMiniTourId !== null}
                disabled={startingMiniTourId !== null}
                className="gap-1.5 rounded-xl"
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
                <span>
                  <Trans>Help</Trans>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onSelect={() => {
                  void handleStartMiniTour('statistics-basics');
                }}
              >
                <Trans>Statistics basics tour</Trans>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  void handleStartMiniTour('statistics-controls');
                }}
              >
                <Trans>Filters and controls tour</Trans>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters Bar */}
      <Card
        className="rounded-xl animate-in"
        style={{ animationDelay: '50ms' }}
        data-tour="statistics-filters"
      >
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Timeframe Selector */}
            <div className="flex items-center gap-2" data-tour="statistics-timeframe">
              <CalendarDaysIcon className="h-4 w-4 text-muted-foreground" />
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  onClick={() => setTimeframe('all')}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-colors',
                    timeframe === 'all'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  )}
                >
                  <Trans>All Time</Trans>
                </button>
                <button
                  onClick={() => setTimeframe('current')}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-colors border-x border-border',
                    timeframe === 'current'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  )}
                >
                  <Trans>In Stock</Trans>
                </button>
                <button
                  onClick={() => setTimeframe('custom')}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-colors',
                    timeframe === 'custom'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  )}
                >
                  <Trans>Custom</Trans>
                </button>
              </div>
            </div>

            {/* Custom Date Range */}
            {timeframe === 'custom' && (
              <div className="flex items-center gap-2" data-tour="statistics-custom-range">
                <Popover open={showFromCalendar} onOpenChange={setShowFromCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8">
                      {customDateFrom
                        ? customDateFrom.toLocaleDateString(i18n.locale, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : t`From`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDateFrom}
                      onSelect={(date) => {
                        setCustomDateFrom(date);
                        setShowFromCalendar(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground">
                  <Trans>to</Trans>
                </span>
                <Popover open={showToCalendar} onOpenChange={setShowToCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8">
                      {customDateTo
                        ? customDateTo.toLocaleDateString(i18n.locale, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : t`To`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDateTo}
                      onSelect={(date) => {
                        setCustomDateTo(date);
                        setShowToCalendar(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {(customDateFrom || customDateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => {
                      setCustomDateFrom(undefined);
                      setCustomDateTo(undefined);
                    }}
                  >
                    <Trans>Clear</Trans>
                  </Button>
                )}
              </div>
            )}

            {/* Sort Selector */}
            <div className="flex items-center gap-2 ml-auto" data-tour="statistics-sort">
              <ArrowsUpDownIcon className="h-4 w-4 text-muted-foreground" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="purchased">{t`Most Purchased`}</option>
                <option value="consumed">{t`Most Consumed`}</option>
                <option value="wasted">{t`Most Wasted`}</option>
                <option value="spent">{t`Most Spent`}</option>
                <option value="wasteRate">{t`Highest Waste Rate`}</option>
                <option value="currentStock">{t`Most in Stock`}</option>
                <option value="name">{t({ message: 'Name', context: 'sort_option' })}</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-1.5 rounded-md hover:bg-muted transition-colors"
                title={sortOrder === 'asc' ? t`Sort ascending` : t`Sort descending`}
              >
                <FunnelIcon
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform',
                    sortOrder === 'asc' && 'rotate-180'
                  )}
                />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary with FlowRing */}
      {displayTotals && displayTotals.totalProducts > 0 && (!isLoading || shouldShowGhostStats) && (
        <div
          className="animate-in"
          style={{ animationDelay: '100ms' }}
          data-tour="statistics-summary"
        >
          <Card className="rounded-2xl overflow-hidden">
            <CardContent className="py-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                {/* FlowRing Visualization */}
                <div className="flex justify-center lg:justify-start">
                  <FlowRing
                    purchased={displayTotals.totalPurchased}
                    consumed={displayTotals.totalConsumed}
                    wasted={displayTotals.totalWasted}
                    size={180}
                  />
                </div>

                {/* Summary Stats Grid */}
                <div className="flex-1 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                  <SummaryCard
                    label={t`Products`}
                    value={displayTotals.totalProducts}
                    icon={ArchiveBoxIcon}
                  />
                  <SummaryCard
                    label={t`Purchased`}
                    value={displayTotals.totalPurchased}
                    icon={ArchiveBoxIcon}
                    color="primary"
                  />
                  <SummaryCard
                    label={t`Consumed`}
                    value={displayTotals.totalConsumed}
                    icon={CheckIcon}
                    color="fresh"
                  />
                  <SummaryCard
                    label={t`Wasted`}
                    value={displayTotals.totalWasted}
                    icon={XMarkIcon}
                    color="expired"
                  />
                  <SummaryCard
                    label={t`Spent`}
                    value={new Intl.NumberFormat(i18n.locale, {
                      style: 'currency',
                      currency: 'USD',
                    }).format(displayTotals.totalSpent / 100)}
                    icon={CurrencyDollarIcon}
                    subtitle={t`${displayTotals.overallWasteRate.toFixed(1)}% waste rate`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !shouldShowGhostStats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="rounded-xl animate-pulse">
              <CardContent className="py-5">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-xl bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-muted rounded" />
                    <div className="h-3 w-24 bg-muted rounded" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card className="rounded-xl">
          <CardContent className="py-8">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                <ExclamationTriangleIcon className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-destructive font-medium">
                {error.message || t`Failed to load product statistics`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Grid */}
      {!error && displayProducts.length > 0 && (!isLoading || shouldShowGhostStats) && (
        <div
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-tour="statistics-products-grid"
        >
          {displayProducts.map((product, index) => (
            <ProductStatsCard
              key={product.productId}
              product={product}
              index={index}
              tourTarget={index === 0 ? 'statistics-product-card' : undefined}
              isGhost={shouldShowGhostStats}
              onClick={() => setSelectedProduct(product)}
            />
          ))}
        </div>
      )}

      {/* Product Detail Dialog */}
      <ProductStatsDialog
        product={selectedProduct}
        open={!!selectedProduct}
        onOpenChange={(open) => !open && setSelectedProduct(null)}
      />

      {/* Empty State */}
      {!isLoading && !error && displayProducts.length === 0 && (
        <Card className="rounded-2xl animate-in fade-in duration-300">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                <ChartBarIcon className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-display text-xl font-semibold text-foreground">
                <Trans>No product data yet</Trans>
              </h3>
              <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
                {timeframe === 'current' ? (
                  <Trans>
                    No products currently in stock. Add some items to your inventory to see
                    statistics.
                  </Trans>
                ) : timeframe === 'custom' ? (
                  <Trans>No product activity found in the selected date range.</Trans>
                ) : (
                  <Trans>Start tracking items to see consumption and waste statistics here.</Trans>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Summary statistic card
 */
function SummaryCard({
  label,
  value,
  icon: Icon,
  color,
  subtitle,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color?: 'primary' | 'fresh' | 'expired';
  subtitle?: string;
}) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    fresh: 'bg-fresh/10 text-fresh',
    expired: 'bg-expired/10 text-expired',
  };

  return (
    <Card className="rounded-xl">
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg',
              color ? colorClasses[color] : 'bg-muted text-muted-foreground'
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-lg font-semibold text-foreground truncate">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Product statistics card
 */
function ProductStatsCard({
  product,
  index,
  tourTarget,
  isGhost,
  onClick,
}: {
  product: ProductStatsItem;
  index: number;
  tourTarget?: string;
  isGhost?: boolean;
  onClick: () => void;
}) {
  const { t, i18n } = useLingui();
  const resolveProductName = useResolveProductName();

  // Resolve localized product name
  const displayName = resolveProductName({
    name: product.productName,
    names: product.productNames ?? undefined,
  });

  const wasteRateColor =
    product.wasteRate > 30
      ? 'text-expired'
      : product.wasteRate > 15
        ? 'text-expiring-soon'
        : 'text-fresh';

  return (
    <Card
      className="rounded-xl hover:shadow-md hover:border-primary/20 transition-all duration-200 animate-in fade-in slide-in-from-bottom-2 cursor-pointer"
      style={{ animationDelay: `${index * 50}ms` }}
      data-tour={tourTarget}
      onClick={onClick}
    >
      <CardContent className="py-5">
        <div className="flex items-start gap-4">
          {/* Product image or placeholder */}
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-muted overflow-hidden">
            {product.productImageUrl ? (
              <img
                src={product.productImageUrl}
                alt={displayName}
                className="h-full w-full object-cover"
              />
            ) : (
              <ArchiveBoxIcon className="h-6 w-6 text-muted-foreground" />
            )}
          </div>

          {/* Product info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-foreground truncate">{displayName}</h3>
            {product.productBrand && (
              <p className="text-sm text-muted-foreground truncate">{product.productBrand}</p>
            )}

            {/* Current stock badge */}
            {product.currentStock > 0 && (
              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-fresh/10 text-fresh">
                <Trans>{product.currentStock} in stock</Trans>
              </span>
            )}
            {isGhost && (
              <span
                className={cn(
                  'inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary',
                  product.currentStock > 0 && 'ml-2'
                )}
              >
                <Trans>Demo</Trans>
              </span>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-lg font-semibold text-foreground">{product.totalPurchased}</p>
              <p className="text-xs text-muted-foreground">
                <Trans>Purchased</Trans>
              </p>
            </div>
            <div>
              <p className="text-lg font-semibold text-fresh">{product.totalConsumed}</p>
              <p className="text-xs text-muted-foreground">
                <Trans>Consumed</Trans>
              </p>
            </div>
            <div>
              <p className="text-lg font-semibold text-expired">{product.totalWasted}</p>
              <p className="text-xs text-muted-foreground">
                <Trans>Wasted</Trans>
              </p>
            </div>
            <div>
              <p className={cn('text-lg font-semibold', wasteRateColor)}>
                {product.wasteRate.toFixed(0)}%
              </p>
              <p className="text-xs text-muted-foreground">
                <Trans>Waste Rate</Trans>
              </p>
            </div>
          </div>

          {/* Spent */}
          {product.totalSpent > 0 && (
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                <Trans>Total spent</Trans>
              </span>
              <span className="font-medium text-foreground">
                {new Intl.NumberFormat(i18n.locale, {
                  style: 'currency',
                  currency: 'USD',
                }).format(product.totalSpent / 100)}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
